/* eslint-disable fp/no-mutating-methods */
/* eslint-disable no-param-reassign */
/* eslint-disable fp/no-mutation */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { append, groupBy, map, pipe, sort } from 'ramda'
import { v4 as uuidv4 } from 'uuid'

import { AppRootState, Status } from 'src/store/store'
import dayOfWeek from 'src/utilities/day-of-week'
import axios, { pluckData } from 'src/utilities/http-client'

type HydratedSession = {
  className: string
  scheduleID: string
  classQuizID: string
  sessionTime: string
  sessionID: string
  classImageName: string
  classMeetingUrl: string
  classTitleScreen: string
  classQuizPasscode: string
}

type HydratedSchedule = {
  id: string
  name: string
  sessions: HydratedSession[]
  expiration: string
}

export type QuizQuestionAnswer = {
  id: string
  text: string
  sequence: number
  isAnswer: boolean
}

export type QuizQuestion = {
  id: string
  text: string
  sequence: number
  choices: QuizQuestionAnswer[]
  explanationText: string
  answerChoiceID: string
  figureImageName?: string
}

type Quiz = {
  id: string
  classID: string
  name: string
  description: string
  passingScore: number
  questions: QuizQuestion[]
  courseCode?: string // deprecated; not in use in V3
}

export type QuizQuestionSelection = {
  answerId: string
  questionId: string
  optionId?: string
}

export type QuizFinalResults = {
  score: number
  isPassing: boolean
}

export let fetchHydratedSchedule = createAsyncThunk(
  'live/fetchHydratedScheduleStatus',
  () => axios.get<HydratedSchedule[]>('/api/v2/live/schedule').then(pluckData),
)

export let fetchQuiz = createAsyncThunk('classroom/fetchQuiz', (id: string) =>
  axios
    .get<Quiz>(`/api/v2/live-class/session/quiz?quizID=${id}`)
    .then(pluckData),
)

type LiveState = {
  hydratedScheduleStatus: Status
  hydratedSchedule: HydratedSchedule[]
  selectedSchedule: number
  activeStep: number
  quizStatus: Status
  quiz: Quiz
  currentQuizQuestion: number
  quizSessionID: string
  currentQuizSelections: QuizQuestionSelection[]
  quizFinalResults: QuizFinalResults
}

export let initialState: LiveState = {
  hydratedScheduleStatus: 'uninitialized',
  hydratedSchedule: [],
  selectedSchedule: 0,
  activeStep: 0,
  quizStatus: 'uninitialized',
  quiz: {
    id: '',
    classID: '',
    name: '',
    description: '',
    passingScore: 0,
    questions: [],
  },
  currentQuizQuestion: 0,
  quizSessionID: uuidv4(),
  currentQuizSelections: [],
  quizFinalResults: {
    score: 0,
    isPassing: false,
  },
}

export let liveSlice = createSlice({
  name: 'live',
  initialState,
  reducers: {
    hydratedScheduleChanged: (
      state,
      action: PayloadAction<HydratedSchedule[]>,
    ) => {
      state.hydratedSchedule = action.payload
    },
    progressResetted: (state) => {
      state.hydratedScheduleStatus = initialState.hydratedScheduleStatus
      state.hydratedSchedule = initialState.hydratedSchedule
      state.selectedSchedule = initialState.selectedSchedule
      state.activeStep = initialState.activeStep
      state.quizStatus = initialState.quizStatus
      state.quiz = initialState.quiz
      state.currentQuizQuestion = initialState.currentQuizQuestion
      state.quizSessionID = initialState.quizSessionID
      state.currentQuizSelections = initialState.currentQuizSelections
      state.quizFinalResults = initialState.quizFinalResults
    },
    selectedScheduleChanged: (state, action: PayloadAction<number>) => {
      state.selectedSchedule = action.payload
    },
    activeStepChanged: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload
    },
    steppedForward: (state) => {
      state.activeStep += 1
    },
    steppedBackward: (state) => {
      state.activeStep -= 1
    },
    quizChanged: (state, action: PayloadAction<Quiz>) => {
      state.quiz = action.payload
    },
    currentQuizQuestionSteppedForward: (state) => {
      state.currentQuizQuestion =
        state.currentQuizQuestion < state.quiz.questions.length
          ? state.currentQuizQuestion + 1
          : state.currentQuizQuestion
    },
    quizSessionStarted: (state) => {
      state.quizSessionID = uuidv4()
    },
    quizQuestionSelectionAdded: (
      state,
      action: PayloadAction<QuizQuestionSelection>,
    ) => {
      state.currentQuizSelections = append(
        action.payload,
        state.currentQuizSelections,
      )
    },
    clearQuizSelections: (state) => {
      state.currentQuizSelections = []
    },
    quizFinalResultsChanged: (
      state,
      action: PayloadAction<QuizFinalResults>,
    ) => {
      state.quizFinalResults = action.payload
    },
    quizRestarted: (state) => {
      state.currentQuizQuestion = initialState.currentQuizQuestion
      state.currentQuizSelections = initialState.currentQuizSelections
      state.quizSessionID = uuidv4()
      state.quizFinalResults = initialState.quizFinalResults
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHydratedSchedule.pending, (state) => {
        state.hydratedScheduleStatus = 'pending'
      })
      .addCase(fetchHydratedSchedule.rejected, (state) => {
        state.hydratedScheduleStatus = 'rejected'
      })
      .addCase(fetchHydratedSchedule.fulfilled, (state, action) => {
        state.hydratedScheduleStatus = 'fulfilled'
        state.hydratedSchedule = action.payload
      })
      .addCase(fetchQuiz.pending, (state) => {
        state.quizStatus = 'pending'
      })
      .addCase(fetchQuiz.rejected, (state) => {
        state.quizStatus = 'rejected'
      })
      .addCase(fetchQuiz.fulfilled, (state, action) => {
        state.quizStatus = 'fulfilled'
        state.quiz = action.payload
      })
  },
})

export let {
  hydratedScheduleChanged,
  selectedScheduleChanged,
  activeStepChanged,
  steppedForward,
  steppedBackward,
  quizChanged,
  currentQuizQuestionSteppedForward,
  quizSessionStarted,
  quizQuestionSelectionAdded,
  clearQuizSelections,
  quizFinalResultsChanged,
  quizRestarted,
  progressResetted,
} = liveSlice.actions

export let selectHydratedScheduleStatus = (state: AppRootState) =>
  state.live.hydratedScheduleStatus

let groupByDOW = groupBy((session: HydratedSession) =>
  dayOfWeek(dayjs(session.sessionTime).utc(true).local().day()),
)

export let selectHydratedSchedule = (state: AppRootState) =>
  pipe(
    sort((a: HydratedSchedule, b: HydratedSchedule) =>
      dayjs(a.expiration).diff(dayjs(b.expiration))
    ),
    map((schedule: HydratedSchedule) => ({
      ...schedule,
      sessions: sort((a: HydratedSession, b: HydratedSession) =>
        a.sessionTime.localeCompare(b.sessionTime),
      )(schedule.sessions),
    })) as any,
    map((schedule: HydratedSchedule) => ({
      ...schedule,
      sessions: groupByDOW(schedule.sessions),
    })),
  )(state.live.hydratedSchedule)

export let selectSelectedSchedule = (state: AppRootState) =>
  state.live.selectedSchedule

export let selectSessionByID = (sessionID: string) => (state: AppRootState) =>
  state.live.hydratedSchedule
    .flatMap((s) => s.sessions)
    .find((s) => s.sessionID === sessionID)

export let selectActiveStep = (state: AppRootState) => state.live.activeStep

export let selectQuizStatus = (state: AppRootState) => state.live.quizStatus
export let selectQuiz = (state: AppRootState) => state.live.quiz
export let selectCurrentQuizQuestion = (state: AppRootState) =>
  state.live.currentQuizQuestion
export let selectQuizSessionID = (state: AppRootState) =>
  state.live.quizSessionID
export let selectCurrentQuizQuestionAtIndex =
  (idx: number) => (state: AppRootState) =>
    state.live.quiz.questions[idx]
export let selectCurrentQuizSelections = (state: AppRootState) =>
  state.live.currentQuizSelections
export let selectQuizFinalResults = (state: AppRootState) =>
  state.live.quizFinalResults

export default liveSlice.reducer
